import { useMemo, useState } from "react";
import { forgotPassword as forgotPasswordApi } from "../api/auth";
import highWindowLogo from "../assets/HighWindow_logo.png";
import loginVideoFile from "../assets/login_vid.mp4";
import useSignedAssetUrls from "../hooks/useSignedAssetUrls";
import { resolveAssetUrl } from "../utils/assets";

const DEFAULT_LOGIN_VIDEO_KEY = "login_vid.mp4";
const loginVideoKey = (process.env.REACT_APP_LOGIN_VIDEO_KEY || DEFAULT_LOGIN_VIDEO_KEY).trim();
const loginVideoUrl = (process.env.REACT_APP_LOGIN_VIDEO_URL || "").trim();

const EMAIL_REGEX = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function LoginScreen({ onSubmit, error, defaultEmail = "", authLoading }) {
  const [email, setEmail] = useState(defaultEmail);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  const [isArabic, setIsArabic] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotStatus, setForgotStatus] = useState("idle");
  const [forgotError, setForgotError] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const signedVideoUrls = useSignedAssetUrls(loginVideoKey ? [loginVideoKey] : []);
  const signedVideoUrl = loginVideoKey ? resolveAssetUrl(loginVideoKey, signedVideoUrls) : "";
  const videoSrc = signedVideoUrl || loginVideoUrl || loginVideoFile;

  const copy = useMemo(
    () =>
      isArabic
        ? {
            heading: "مرحباً بكم في هاي ويندو",
            emailLabel: "البريد الإلكتروني",
            emailPlaceholder: "name@email.com",
            passwordLabel: "كلمة المرور",
            togglePassword: showPassword ? "إخفاء" : "إظهار",
            remember: "تذكرني على هذا الجهاز",
            forgot: "نسيت كلمة المرور؟",
            forgotPill: "إعادة التعيين",
            forgotTitle: "إعادة تعيين كلمة المرور",
            forgotMessage: "أدخل البريد الإلكتروني المرتبط بحسابك. سنرسل لك كلمة مرور مؤقتة.",
            forgotEmailLabel: "البريد الإلكتروني",
            forgotEmailPlaceholder: "name@example.com",
            forgotSend: "إرسال",
            forgotCancel: "إلغاء",
            forgotSuccess: "إذا كان هذا البريد مسجلاً لدينا، فستصلك رسالة تحتوي على كلمة مرور مؤقتة.",
            forgotInvalidEmail: "يرجى إدخال بريد إلكتروني صالح.",
            forgotFailure: "تعذر إرسال رسالة إعادة التعيين. حاول مرة أخرى.",
            login: "تسجيل الدخول",
            finePrint:
              "إذا واجهت أي مشكلة، يرجى التواصل مع المسؤول للحصول على المساعدة؛ سيساعدك فريق الدعم في برايم تك عند الحاجة.",
            systemSupport: "دعم النظام",
            languageToggle: "English ▾",
          }
        : {
            heading: "Welcome to High Window",
            emailLabel: "Email",
            emailPlaceholder: "name@email.com",
            passwordLabel: "Password",
            togglePassword: showPassword ? "Hide" : "Show",
            remember: "Remember me on this device",
            forgot: "Forgot password?",
            forgotPill: "Password reset",
            forgotTitle: "Reset your password",
            forgotMessage:
              "Enter the email address associated with your account. We'll send you a temporary password.",
            forgotEmailLabel: "Email",
            forgotEmailPlaceholder: "name@example.com",
            forgotSend: "Send reset email",
            forgotCancel: "Cancel",
            forgotSuccess:
              "If an account exists for this email, you'll receive a message with a temporary password.",
            forgotInvalidEmail: "Please enter a valid email address.",
            forgotFailure: "Unable to send reset email. Please try again.",
            login: "Login",
            finePrint:
              "If you encounter a problem, please contact your administrator for assistance; the technical support team from Prime Tech will help as needed.",
            systemSupport: "System Support",
            languageToggle: "عربي ▾",
          },
    [isArabic, showPassword]
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      await onSubmit(email, password, { remember });
    } catch (err) {
      // Ensure unexpected errors don't break the UI; parent handles errors via props
      console.error("Login submission failed", err);
    }
  };

  const openForgotPassword = () => {
    setForgotEmail(email || defaultEmail || "");
    setForgotError("");
    setForgotStatus("idle");
    setForgotOpen(true);
  };

  const closeForgotPassword = () => {
    if (forgotLoading) return;
    setForgotOpen(false);
    setForgotError("");
    setForgotStatus("idle");
  };

  const handleForgotSubmit = async (event) => {
    event.preventDefault();
    const trimmedEmail = forgotEmail.trim().toLowerCase();

    if (!EMAIL_REGEX.test(trimmedEmail)) {
      setForgotError(copy.forgotInvalidEmail);
      setForgotStatus("idle");
      return;
    }

    setForgotLoading(true);
    setForgotError("");
    try {
      await forgotPasswordApi({ email: trimmedEmail });
      setForgotStatus("sent");
    } catch (err) {
      setForgotError(err?.message || copy.forgotFailure);
      setForgotStatus("idle");
    } finally {
      setForgotLoading(false);
    }
  };

  const handleForgotOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      closeForgotPassword();
    }
  };

  return (
    <div
      className={`login-shell ${isArabic ? "rtl" : ""}`}
      dir={isArabic ? "rtl" : "ltr"}
      lang={isArabic ? "ar" : "en"}
    >
      <section className="login-hero">
        <div className="brand-row">
          <img src={highWindowLogo} alt="High Window logo" className="brand-logo login-logo" />
        </div>

        <div className="welcome-copy">
          <h1>{copy.heading}</h1>
        </div>

        <form className="login-form" onSubmit={handleSubmit}>
          <label className="field">
            <span className="field-label">{copy.emailLabel}</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={copy.emailPlaceholder}
              required
            />
          </label>

          <label className="field">
            <span className="field-label">{copy.passwordLabel}</span>
            <div className="input-with-icon">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={copy.passwordLabel}
                required
              />
              <button
                type="button"
                className="ghost-link input-action"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={
                  isArabic
                    ? showPassword
                      ? "إخفاء كلمة المرور"
                      : "إظهار كلمة المرور"
                    : showPassword
                    ? "Hide password"
                    : "Show password"
                }
              >
                {copy.togglePassword}
              </button>
            </div>
          </label>

          <div className="login-meta">
            <label className="checkbox">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
              />
              <span>{copy.remember}</span>
            </label>
            <button type="button" className="ghost-link subtle" onClick={openForgotPassword}>
              {copy.forgot}
            </button>
          </div>

          {error ? <div className="error-banner">{error}</div> : null}

          <button type="submit" className="primary-btn wide" disabled={authLoading} aria-busy={authLoading}>
            {authLoading ? `${copy.login}...` : copy.login}
          </button>

          <p className="fine-print centered">
            {copy.finePrint}
          </p>

          <div className="login-footer-links">
            <a
              className="ghost-link subtle"
              href="mailto:contact@primetechkw.com?subject=High%20Window%20Medical%20Services%20-%20ADMIN"
            >
              {copy.systemSupport}
            </a>
            <button type="button" className="ghost-link subtle" onClick={() => setIsArabic((prev) => !prev)}>
              {copy.languageToggle}
            </button>
          </div>
        </form>
      </section>

      <section className="hero-visual">
        {videoSrc ? (
          <video
            className="hero-video"
            src={videoSrc}
            autoPlay
            muted
            loop
            playsInline
            aria-hidden="true"
          />
        ) : null}
      </section>

      {forgotOpen ? (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="forgot-title" onClick={handleForgotOverlayClick}>
          <div className="confirm-modal">
            <div className="confirm-content">
              <div className="confirm-pill">{copy.forgotPill}</div>
              <h3 id="forgot-title" className="confirm-title">
                {copy.forgotTitle}
              </h3>
              <p className="confirm-message">{copy.forgotMessage}</p>
            </div>
            <form className="login-form forgot-form" onSubmit={handleForgotSubmit}>
              <label className="field">
                <span className="field-label">{copy.forgotEmailLabel}</span>
                <input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => {
                    setForgotEmail(e.target.value);
                    if (forgotStatus === "sent") setForgotStatus("idle");
                  }}
                  placeholder={copy.forgotEmailPlaceholder}
                  autoComplete="email"
                  autoFocus
                  required
                />
              </label>
              {forgotError ? <div className="error-banner">{forgotError}</div> : null}
              {forgotStatus === "sent" ? (
                <div className="inline-alert success">{copy.forgotSuccess}</div>
              ) : null}
              <div className="confirm-actions">
                <button className="ghost-btn" type="button" onClick={closeForgotPassword} disabled={forgotLoading}>
                  {copy.forgotCancel}
                </button>
                <button className="primary-btn" type="submit" disabled={forgotLoading}>
                  {forgotLoading ? `${copy.forgotSend}...` : copy.forgotSend}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default LoginScreen;
